import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/ko-plus";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const koPlus: CaseStudy = {
  slug: "ko-plus",
  title: "Координатор плюс",
  cardTitle: "Разработка сайта противопожарной компании «Координатор плюс»",
  subtitle: "Инженерные системы и пожарная безопасность",
  industry: { label: "Строительство и архитектура", slug: "construction" },
  eyebrow: "Корпоративный сайт · Инженерная компания",
  h1: "Разработка сайта противопожарной компании «Координатор плюс»",
  lead:
    "Разработали корпоративный сайт инженерной компании «Координатор плюс»: услуги проектирования, монтажа и обслуживания систем пожарной, охранной безопасности и видеонаблюдения, план работ и заявку для B2B-клиента.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить сайт инженерной компании" },

  seo: {
    primaryKeyword: "разработка сайта противопожарной компании",
    secondaryKeywords: [
      "сайт компании пожарной безопасности",
      "разработка сайта инженерной компании",
      "сайт монтажной компании B2B",
      "корпоративный сайт систем безопасности",
    ],
    semanticEntities: [
      "автоматическая пожарная сигнализация",
      "видеонаблюдение",
      "охранные системы",
      "проектирование",
      "монтаж",
      "техническое обслуживание",
      "аудит и смета",
    ],
    excludedKeywords: [
      "пожарная сигнализация монтаж цена",
      "огнетушитель купить",
      "АПС монтаж",
      "видеонаблюдение цена",
    ],
    title: "Сайт «Координатор плюс» — кейс HAY.agency",
    description:
      "Кейс HAY.agency: корпоративный сайт инженерной компании с услугами проектирования, монтажа и обслуживания систем пожарной, охранной безопасности и видеонаблюдения.",
    h1: "Разработка сайта противопожарной компании «Координатор плюс»",
    slug: "ko-plus",
    canonicalUrl: `${SITE_URL}/cases/ko-plus`,
    ogTitle: "Сайт инженерной компании «Координатор плюс»",
    ogDescription:
      "Проектирование, монтаж и обслуживание систем пожарной, охранной безопасности и видеонаблюдения на одном корпоративном сайте.",
    ogImage: `${SITE_URL}${dir}/ko-plus-og.webp`,
    robotsIndex: true,
    datePublished: "2024-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка сайтов",
      to: routes.sites,
      note: "Корпоративный сайт инженерной компании",
    },
    relatedIndustry: {
      label: "SEO-продвижение сайтов",
      to: routes.seo,
      note: "Видимость услуг для B2B-клиентов",
    },
    relatedCases: [],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен сайт инженерной или монтажной компании?",
    text: "Спроектируем структуру услуг, план работ и заявку для B2B-клиента.",
    button: "Обсудить сайт инженерной компании",
  },

  client: {
    name: "Координатор плюс",
    url: "https://ko-plus.ru/",
    city: "Россия",
    industry: "Инженерные системы и пожарная безопасность",
  },
  year: "2024",
  services: [
    "Дизайн корпоративного сайта",
    "Разработка структуры услуг",
    "Раздел плана работ",
    "Форма заявки для B2B-клиента",
  ],
  platform: "1С-Битрикс",
  tags: [
    { label: "Корпоративный сайт", to: routes.corporate },
    { label: "Разработка", to: routes.sites },
  ],
  facts: [
    { label: "Клиент", value: "Координатор плюс" },
    { label: "Тип проекта", value: "Корпоративный сайт инженерной компании" },
    { label: "Платформа", value: "1С-Битрикс" },
    { label: "Год", value: "2024" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Корпоративный сайт для компании, работающей на промышленных и общественных объектах",
      "Структура услуг: проектирование, монтаж и техническое обслуживание",
      "Раздел систем: пожарная сигнализация, видеонаблюдение и охранные системы",
      "План работ от аудита и сметы до проектирования и монтажа",
      "Форма заявки для B2B-клиента",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "«Координатор плюс» — инженерная компания, которая проектирует, монтирует и обслуживает системы пожарной и охранной безопасности, а также видеонаблюдение на промышленных и общественных объектах. Сайт должен приводить B2B-клиентов, которые ищут подрядчика на комплекс работ, а не частных заказчиков с точечным запросом.",
        "Вторая часть задачи — показать понятный план работ: от аудита и сметы до проектирования и монтажа, чтобы клиент видел весь цикл сотрудничества с компанией.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: индустриальная подача без fire-иконографии",
    paragraphs: [
      "Интерфейс собран на тёпло-серой индустриальной подложке с реальными объектами и инженерными системами — без визуальных клише вроде красных значков огня.",
      "Жизненный цикл системы и план работ поданы горизонтальными схемами, чтобы B2B-клиент быстро считывал масштаб и последовательность работ.",
    ],
    bullets: [
      "Тёпло-серая индустриальная подложка вместо белого фона",
      "Горизонтальная схема жизненного цикла системы",
      "Понятный план работ от аудита до монтажа",
    ],
  },

  identity: {
    fonts: [
      { name: "Geometria", role: "Заголовки", sample: "Координатор плюс" },
      { name: "Circe", role: "Текст и интерфейс", sample: "Проектирование и монтаж" },
    ],
    colors: [
      { hex: "#EDEBE6", name: "Фон" },
      { hex: "#5B5A56", name: "Индустриальный акцент" },
      { hex: "#1D1C1A", name: "Текст" },
    ],
  },

  cover: img(
    "ko-plus-case-cover",
    "Главная страница сайта инженерной компании «Координатор плюс»",
    6,
  ),

  gallery: [
    img("ko-plus-lifecycle", "Жизненный цикл системы: проектирование, монтаж, обслуживание", 6),
    img("ko-plus-systems", "Типы систем: пожарная, видеонаблюдение, охранная", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия сохраняет логику десктопа: смысловые карточки услуг и систем идут горизонтальным скроллом.",
      "Раздел плана работ на мобильной версии собран в отдельную компактную композицию.",
    ],
    images: [
      mobile("ko-plus-mobile-hero", "Мобильная главная сайта «Координатор плюс»"),
      mobile("ko-plus-mobile-workplan", "Мобильный план работ «Координатор плюс»"),
    ],
  },

  columnBlocks: [
    {
      id: "lifecycle",
      title: "Жизненный цикл системы",
      paragraphs: [
        "Проектирование, монтаж и техническое обслуживание показаны одной горизонтальной схемой — клиент сразу видит весь цикл работы с компанией.",
      ],
      bullets: ["Проектирование", "Монтаж", "Техническое обслуживание"],
    },
    {
      id: "systems",
      title: "Типы систем",
      paragraphs: [
        "Три карточки систем — пожарная сигнализация, видеонаблюдение и охранные системы — дают клиенту быстрый вход в нужное направление.",
      ],
      columns: [
        { title: "Пожарная безопасность", items: ["Автоматическая пожарная сигнализация"] },
        { title: "Видео", items: ["Системы видеонаблюдения"] },
        { title: "Охрана", items: ["Охранные системы"] },
      ],
    },
    {
      id: "workplan",
      title: "План работ",
      paragraphs: [
        "Аудит, смета, проект и монтаж показаны коротко, без длинного текста — понятная последовательность для B2B-клиента.",
      ],
      bullets: ["Аудит объекта", "Смета", "Проектирование", "Монтаж"],
    },
    {
      id: "request",
      title: "Заявка B2B",
      paragraphs: [
        "Контакт и форма заявки собраны коротко — клиент оставляет заявку без лишних полей.",
      ],
      bullets: ["Форма заявки", "Контактные данные компании"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [{ label: "Разработка сайта", to: routes.sites }],
  },
};
